<template>
  <!-- System Designer — no Figma frame; built in the Service & Repair card style.
       Flow follows the Android technician app: DashboardFragment "System Designer" → single-active-record check →
       sales/presentation → SalesItemListFragment (Our Promise / Approach / Purpose / Product, Your Home / Summary / Opinion).
       "Your Home" is the server create-quote flow (same iframe modal as Create Quote → Replacement Quote). -->
  <div class="w-full font-roboto">
    <section class="sdCard">
      <div class="flex items-center gap-1 pb-[15px]">
        <span class="w-[8px] h-[8px] rounded-full bg-[#A084E8]" />
        <span class="w-[35px] h-[8px] rounded-full bg-[#A084E8]" />
        <h2 class="m-0 pl-2 text-[18px] laptop:text-[20px] font-semibold text-[#333333]">System Designer</h2>
      </div>

      <p v-if="!isAllowed" class="m-0 py-[30px] text-center text-[16px] text-[#333333]">
        System Designer is not available for your account.
      </p>

      <!-- Android shows the active-job dialog instead of opening the screen -->
      <div v-else-if="blocked" class="fxInfoBox flex-col tablet:flex-row tablet:items-center">
        <div class="flex-1">
          <p class="m-0 font-medium">Dispositioned Leads</p>
          <p class="m-0 mt-[4px]">{{ blocked.message }}</p>
        </div>
        <div class="flex flex-wrap gap-[10px]">
          <button v-if="blocked.active_record?.id" type="button" class="fxBtnPrimary" @click="openActiveRecord(blocked.active_record)">
            {{ blocked.active_record.button_text || 'Resolve' }}
          </button>
          <button type="button" class="fxBtnSecondary" @click="load">Try Again</button>
        </div>
      </div>

      <div v-else-if="loadError" class="flex flex-col items-center gap-[15px] py-[30px]">
        <p class="m-0 text-[16px] text-[#333333] text-center">{{ loadError }}</p>
        <button type="button" class="fxBtnSecondary" @click="load">Try Again</button>
      </div>

      <template v-else-if="urls">
        <p class="m-0 pb-[15px] text-[14px] text-[#666666]">
          Walk the homeowner through the presentation, then design their new system and create a quote.
        </p>
        <div class="grid grid-cols-2 tablet:grid-cols-3 laptop:grid-cols-4 gap-[12px] laptop:gap-[20px]">
          <button v-for="tile in tiles" :key="tile.key" type="button" class="sdTile group" @click="openTile(tile.key)">
            <span class="sdTileIcon" :style="{ background: tile.color }"><img :src="tile.icon" alt=""></span>
            <span class="text-[15px] laptop:text-[16px] font-medium text-[#333333]">{{ tile.label }}</span>
            <span class="mobile:hidden tablet:block text-[12px] text-[#666666] leading-[16px]">{{ tile.hint }}</span>
          </button>
        </div>
      </template>
    </section>

    <!-- Our Promise / Approach / Purpose / Product -->
    <div ref="sdModal" class="modal fade fxModal" tabindex="-1" aria-hidden="true" data-bs-backdrop="static">
      <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable max-w-full tablet:!max-w-[90vw] largeDesktop:!max-w-[1200px] mobile:mx-[16px] tablet:mx-auto">
        <div class="modal-content">
          <div class="modal-body !p-[15px] font-roboto">
            <FxModalHeader :title="page.title" :dismiss="false" @close="closeModal" />

            <!-- Our Product: company banners or video, then the product PDFs -->
            <template v-if="page.key === 'product' && !page.isShop">
              <div v-if="media.banners.length" class="relative w-full rounded-[10px] overflow-hidden bg-[#F4F5FB] mb-[15px]">
                <img :src="media.banners[slide]" alt="Product banner" class="w-full h-[180px] tablet:h-[260px] laptop:h-[320px] object-contain">
                <template v-if="media.banners.length > 1">
                  <button type="button" class="sdArrow left-[8px]" aria-label="Previous banner" @click="moveSlide(-1)">
                    <i class="fa fa-angle-left" />
                  </button>
                  <button type="button" class="sdArrow right-[8px]" aria-label="Next banner" @click="moveSlide(1)">
                    <i class="fa fa-angle-right" />
                  </button>
                  <div class="absolute bottom-[8px] left-0 right-0 flex justify-center gap-[6px]">
                    <button v-for="(b, i) in media.banners" :key="b + i" type="button" :aria-label="`Banner ${i + 1}`"
                      class="w-[8px] h-[8px] rounded-full border-0 p-0" :class="i === slide ? 'bg-[#7695FF]' : 'bg-[#CCCCCC]'"
                      @click="slide = i" />
                  </div>
                </template>
              </div>
              <video v-else-if="media.video" :src="media.video" class="w-full max-h-[320px] rounded-[10px] bg-black mb-[15px]"
                controls autoplay muted playsinline loop />
              <div v-if="media.pdf.length > 1" class="mb-[15px]">
                <label class="sdLabel" for="sdPdf">Product Documents</label>
                <FxSelect v-model="page.url" :options="media.pdf" label-key="title" />
              </div>
            </template>

            <iframe v-if="page.url" :key="page.url" :src="frameSrc(page.url)" :title="page.title"
              class="w-full border-0 rounded-[8px]" :class="page.key === 'product' && !page.isShop ? 'h-[60vh]' : 'h-[70vh]'" />

            <!-- Our Promise: customer reviews -->
            <div v-if="page.key === 'promise' && !page.isShop" class="mt-[20px]">
              <p class="m-0 mb-[10px] text-[16px] font-medium text-[#333333]">Customer Reviews</p>
              <p v-if="!reviews.length" class="m-0 text-[14px] text-[#666666]">No Reviews found.</p>
              <div v-else class="flex gap-[12px] overflow-x-auto customScrollbar pb-[8px]">
                <div v-for="(r, i) in reviews" :key="i"
                  class="flex-shrink-0 w-[260px] tablet:w-[300px] rounded-[12px] border-1 border-[#EFEFEF] p-[12px]">
                  <div class="flex items-center gap-[10px]">
                    <img v-if="r.consumer_picture" :src="r.consumer_picture" alt="" class="w-[40px] h-[40px] rounded-full object-cover">
                    <div class="min-w-0">
                      <p class="m-0 text-[14px] font-medium text-[#333333] truncate">{{ r.consumer_name }}</p>
                      <p class="m-0 text-[13px] text-[#F5A623]" :aria-label="`Rating ${r.rating}`">
                        <i v-for="n in 5" :key="n" class="fa" :class="n <= Math.round(Number(r.rating) || 0) ? 'fa-star' : 'fa-star-o'" />
                        <span class="ml-[4px] text-[#666666]">{{ Number(r.rating || 0).toFixed(1) }}</span>
                      </p>
                    </div>
                  </div>
                  <p class="m-0 mt-[8px] text-[13px] leading-[18px] text-[#666666]">{{ r.review }}</p>
                </div>
              </div>
            </div>

            <div class="flex flex-wrap items-center justify-between gap-[10px] mt-[15px]">
              <a v-if="page.url" :href="page.url" target="_blank" rel="noopener noreferrer"
                class="text-[14px] text-[#6987EB] no-underline hover:underline">Open in a new tab</a>
              <span v-else />
              <button v-if="page.next" type="button" class="fxBtnPrimary" @click="goNext">
                {{ page.next === 'home' ? 'Create a New Order' : 'Next' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Your Home / Your Summary: server create-quote flow -->
    <addReplacementQuote ref="createRQRef" @closed="refreshUrls" />
  </div>
</template>

<script>
import { Modal } from "bootstrap";
import { mapGetters } from "vuex";
import { useToast } from "vue-toastification";
import FxModalHeader from "../../components/nest_components/FxModalHeader.vue";
import FxSelect from "../../components/partialComponent/FxSelect.vue";
import addReplacementQuote from "../../components/workOrder/addReplacementQuote.vue";
import { systemDesignerApi } from "../../services/systemDesignerApi";
import { menuHasRoute } from "../../helpers/techMenu";
import promiseIcon from "../../assets/images/system-designer/promise.png";
import approachIcon from "../../assets/images/system-designer/approach.png";
import purposeIcon from "../../assets/images/system-designer/purpose.png";
import productIcon from "../../assets/images/system-designer/product.png";
import homeIcon from "../../assets/images/system-designer/home.png";
import summaryIcon from "../../assets/images/system-designer/summary.png";
import opinionIcon from "../../assets/images/system-designer/opinion.png";

const toast = useToast();

// Android "Next" order: Our Promise → Our Approach → Our Purpose → Our Product → Create a new order (Your Home)
const NEXT = { promise: "approach", approach: "purpose", purpose: "product", product: "home" };
const TITLES = { promise: "Our Promise", approach: "Our Approach", purpose: "Our Purpose", product: "Our Product" };
const URL_KEYS = { promise: "our_promise", approach: "our_approach", purpose: "our_purpose", product: "our_product" };

export default {
  name: "SystemDesigner",
  components: { FxModalHeader, addReplacementQuote, FxSelect },
  data() {
    return {
      urls: null,
      blocked: null,
      loadError: "",
      reviews: [],
      media: { banners: [], video: "", pdf: [] },
      slide: 0,
      page: { key: "", title: "", url: "", next: "", isShop: false },
      tiles: [
        { key: "promise", label: "Our Promise", hint: "What we promise and what customers say", icon: promiseIcon, color: "linear-gradient(135deg, #A084E8, #7695FF)" },
        { key: "approach", label: "Our Approach", hint: "How we work in your home", icon: approachIcon, color: "linear-gradient(135deg, #7695FF, #16BCFF)" },
        { key: "purpose", label: "Our Purpose", hint: "Why we do what we do", icon: purposeIcon, color: "linear-gradient(135deg, #16BCFF, #00B7C2)" },
        { key: "product", label: "Our Product", hint: "Systems and product documents", icon: productIcon, color: "linear-gradient(135deg, #00B7C2, #1FA971)" },
        { key: "home", label: "Your Home", hint: "Design a system and create a new order", icon: homeIcon, color: "linear-gradient(135deg, #F67280, #EA8A8A)" },
        { key: "summary", label: "Your Summary", hint: "Review the quote summary", icon: summaryIcon, color: "linear-gradient(135deg, #9B7EA4, #A084E8)" },
        { key: "opinion", label: "Your Opinion", hint: "Homeowner feedback", icon: opinionIcon, color: "linear-gradient(135deg, #5B616B, #8A919C)" },
      ],
    };
  },
  computed: {
    ...mapGetters(["sideMenuData"]),
    // the side menu is role-filtered on the server (G-25); a direct URL follows it once the menu has loaded
    isAllowed() {
      // opened from a job's "Installation Pricing" (Android SalesItemListFragment with a booking): no menu gate
      if (this.bookingId) return true;
      return !Array.isArray(this.sideMenuData) || menuHasRoute(this.sideMenuData, "/system-designer");
    },
    bookingId() {
      return this.$route.query.booking_id || "";
    },
    isShop() {
      return Number(this.urls?.is_shop_available) === 1;
    },
  },
  mounted() {
    this.load();
  },
  beforeUnmount() {
    this.bsModal?.dispose();
  },
  methods: {
    // Android checkSingleActiveRecordThenSubmitPerfectMatch → getStaticUrls
    async load() {
      this.blocked = null;
      this.loadError = "";
      // Android "Installation Pricing" opens the presentation straight from the job, without the active-job check
      if (this.bookingId) {
        await this.refreshUrls();
        return;
      }
      const guard = await systemDesignerApi.singleActiveRecord();
      if (!guard.ok) {
        if (guard.activeRecord) {
          this.blocked = guard.activeRecord;
          this.showBlocked();
        } else this.loadError = guard.message || "Something went wrong. Please try again.";
        return;
      }
      await this.refreshUrls();
    },
    // also run after the create flow closes, like SalesItemListFragment on back, so the next flow gets a fresh URL
    async refreshUrls() {
      const res = await systemDesignerApi.presentation(this.bookingId);
      if (!res.ok || !res.body) {
        if (!this.urls) this.loadError = res.message || "Unable to load the System Designer.";
        return;
      }
      const urls = res.body;
      // Android SalesItemListFragment: Your Summary / Your Home carry the booking id
      if (this.bookingId) {
        if (urls.your_summary?.init) urls.your_summary.init += "&booking_id=" + this.bookingId;
        if (urls.your_home?.init) urls.your_home.init += "&booking_id=" + this.bookingId;
      }
      this.urls = urls;
    },
    // Android: the "Dispositioned Leads" dialog (same popup as the header create flows); the card stays as the fallback
    showBlocked() {
      const rec = this.blocked?.active_record || {};
      this.$dialog.show({
        title: "Dispositioned Leads",
        text: this.blocked?.message,
        buttonText: rec.button_text || "Resolve",
        onConfirm: () => rec.id && this.openActiveRecord(rec),
      });
    },
    openActiveRecord(record) {
      const paths = { appointment: "/appointment/detail/", booking: "/job/detail/" };
      this.$router.push((paths[record.type] || "/replacement-quote/detail/") + record.id);
    },

    openTile(key) {
      const u = this.urls || {};
      if (key === "home") return this.openCreate(u.your_home?.init, "Create a New Order");
      if (key === "summary") {
        if (u.your_summary?.init) return this.openCreate(u.your_summary.init, "Your Summary");
        // Android alterForSystemConfigChange
        return this.$dialog.show({
          title: "Alert",
          text: "We could not find any previous quote created.",
          buttonText: "Create new quote",
          onConfirm: () => this.openCreate(u.your_home?.init, "Create a New Order"),
        });
      }
      if (key === "opinion") {
        return this.$dialog.show({ title: "Alert", text: "Coming Soon", buttonText: "OK", onConfirm: () => {} });
      }
      return this.openPage(key);
    },

    // shop companies: the company site page in a frame (Android WebViewFragment, no Next);
    // otherwise the presentation page with Next, plus reviews (Promise) or banners/video/PDFs (Product)
    async openPage(key) {
      const url = this.urls?.[URL_KEYS[key]] || "";
      const isShop = this.isShop;
      if ((isShop || key !== "product") && !url) {
        toast.error(`${TITLES[key]} is not available right now.`);
        return;
      }
      this.page = { key, title: TITLES[key], url: key === "product" && !isShop ? "" : url, next: isShop ? "" : NEXT[key], isShop };
      if (!isShop && key === "promise") this.loadReviews();
      if (!isShop && key === "product") await this.loadMedia();
      this.showModal();
    },
    async loadReviews() {
      const res = await systemDesignerApi.reviews();
      this.reviews = res.ok && Array.isArray(res.body) ? res.body : [];
    },
    async loadMedia() {
      const res = await systemDesignerApi.banner();
      const body = res.ok && res.body ? res.body : {};
      const banners = Array.isArray(body.banners) ? body.banners.map(b => (typeof b === "string" ? b : b?.value)).filter(Boolean) : [];
      this.media = {
        banners,
        video: banners.length ? "" : (body.video || ""),
        pdf: Array.isArray(body.pdf) ? body.pdf.filter(p => p?.value) : [],
      };
      this.slide = 0;
      this.page.url = this.media.pdf[0]?.value || "";
    },
    moveSlide(step) {
      const n = this.media.banners.length;
      this.slide = (this.slide + step + n) % n;
    },
    // PDFs: the browser viewer on laptop+, Google's viewer on smaller screens (as Android does), where frames don't render PDFs
    frameSrc(url) {
      const isPdf = /\.pdf($|\?)/i.test(url);
      if (isPdf && window.innerWidth < 1024) return `https://docs.google.com/viewer?embedded=true&url=${encodeURIComponent(url)}`;
      return url;
    },

    goNext() {
      const next = this.page.next;
      if (!next) return;
      if (next === "home") {
        // close this modal first, then open the create flow
        this.$refs.sdModal.addEventListener("hidden.bs.modal", () => this.openCreate(this.urls?.your_home?.init, "Create a New Order"), { once: true });
        this.closeModal();
        return;
      }
      this.openPage(next);
    },
    openCreate(url, title) {
      if (!url) {
        toast.error("You can't create a new order right now.");
        return;
      }
      this.$refs.createRQRef.showModal(url, title);
    },

    showModal() {
      this.$nextTick(() => {
        this.bsModal = this.bsModal || new Modal(this.$refs.sdModal);
        this.bsModal.show();
      });
    },
    closeModal() {
      this.bsModal?.hide();
      this.page = { key: "", title: "", url: "", next: "", isShop: false };
    },
  },
};
</script>

<style scoped>
.sdCard {
  box-shadow: 0px 0px 10px 0px #0000001A;
  border-radius: 20px;
  padding: 20px 15px;
  background: #fff;
}

.sdTile {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 18px 10px;
  border: 1px solid #EFEFEF;
  border-radius: 20px;
  background: #fff;
  text-align: center;
  transition: border-color .2s, box-shadow .2s;
}

.sdTile:hover {
  border-color: #7695FF;
  box-shadow: 0px 0px 10px 0px #0000001A;
}

.sdTileIcon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sdTileIcon img {
  width: 34px;
  height: 34px;
  object-fit: contain;
}

.sdArrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, .9);
  color: #333333;
  font-size: 20px;
  box-shadow: 0px 0px 10px 0px #0000001A;
}

.sdLabel {
  display: block;
  margin-bottom: 4px;
  font-size: 14px;
  color: #EA8A8A;
}

@media (min-width: 1024px) {
  .sdCard {
    padding: 20px;
  }

  .sdTileIcon {
    width: 76px;
    height: 76px;
  }

  .sdTileIcon img {
    width: 40px;
    height: 40px;
  }
}
</style>
